'use client'

import { Dispatch, SetStateAction, useState } from 'react'

import { ExtendedBook, ExtendedListing, ShippingPrice } from '@hermes/types'
import { Box } from '@hermes/web-components'

import AlertsUnsubscribe from '@components/AlertsUnsubscribeModal'
import { SellerListingCard } from '@components/SellerListingCard'
import useChooseDeliveryService from '@hooks/useChooseDeliveryService'
import { useNavigation } from '@hooks/useNavigation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useUserToken } from '@hooks/useUserToken'
import { useAnalytics } from '@lib/contexts/analyticsContext'
import { useFlashMessage } from '@lib/contexts/flashMessageContext'
import { useUser } from '@lib/contexts/userContext'

import { useYourWaitingList } from '@hermes/api-sdk'
import { useRecommendedSellersV2 } from '@hooks/useRecommendedSellerV2'
import { useCheckoutStore } from '@lib/stores/checkout'
import { useIntercom } from '@hooks/useIntercom'
import { BuyButton } from './ActionSection.styles'

type Variants = 'outlined' | 'contained'

const getOtherVersionsButtonVariant = (
  isLoading: boolean,
  hasSales: boolean,
  hasRecommendedSeller: boolean
): Variants => {
  if (isLoading) {
    return 'outlined'
  }

  if (hasSales) {
    return 'outlined'
  }

  return hasRecommendedSeller ? 'outlined' : 'contained'
}

const getRecommendedSellerListing = (recommendedSellers: ExtendedListing[] | null, hasShippingSeller: boolean) => {
  if (recommendedSellers && recommendedSellers?.length > 0 && hasShippingSeller) {
    const sellerWithBadge = recommendedSellers.find((listing) => listing.badges?.recommended)
    const cheapestSeller = recommendedSellers.sort((a, b) => a.price - b.price)[0]
    return sellerWithBadge || cheapestSeller || recommendedSellers[0]
  }

  return null
}

interface MainActionProps {
  setVersionsModalShow: Dispatch<SetStateAction<boolean>>
  bookDetails: ExtendedBook
  shippingPrice: ShippingPrice
}

const MainActions = ({ setVersionsModalShow, bookDetails, shippingPrice }: MainActionProps) => {
  const { t } = useTranslateMessage()
  const { user } = useUser()
  const { token } = useUserToken()
  const analytics = useAnalytics()
  const flashMessage = useFlashMessage()
  const { data: groupedRecommendedSellers, isLoading } = useRecommendedSellersV2({
    size: 1,
    bookUuid: bookDetails.uuid
  })
  const recommendedSellers = groupedRecommendedSellers.all

  const addToCart = useCheckoutStore((state) => state.addToCart)
  const clearCart = useCheckoutStore((state) => state.clearCart)
  const { navigate } = useNavigation()

  const [unsubNotificationModalView, setUnsubNotificationModalView] = useState(false)

  const bookIsAvailable = !!recommendedSellers.length

  const hasShippingSeller = !!groupedRecommendedSellers.shipping.length

  const hasMeetupSeller = !!groupedRecommendedSellers.meetup.length

  const bookHasOtherVersions = bookDetails.versions && Boolean(bookDetails.versions?.length)

  const recommendedSellerListing = getRecommendedSellerListing(groupedRecommendedSellers.shipping, hasShippingSeller)

  const hasRecommendedSeller = Boolean(recommendedSellerListing)

  const { hasItem, addItem, removeItem } = useYourWaitingList()

  const navigateFromRecommendedSellerClicked = () => {
    if (!recommendedSellerListing || !recommendedSellerListing.seller.listings) {
      return
    }
    const sellerUuid = recommendedSellerListing.seller.uuid
    if (recommendedSellerListing?.seller?.listings && recommendedSellerListing.seller.listings.length >= 1) {
      navigate.moreFromSellerPage(sellerUuid)
    } else {
      navigate.checkoutPage(sellerUuid)
    }
  }

  const { ChooseDeliveryServiceModal, setIsOpen: setChooseDeliveryIsOpen } = useChooseDeliveryService({
    shippingPrice,
    onCloseFn: navigateFromRecommendedSellerClicked
  })

  const recommendedSellerClicked = (listing: ExtendedListing) => {
    clearCart()
    addToCart(listing)
    setChooseDeliveryIsOpen(listing)
    analytics.trackEvent('PRODUCT_ADDED_TO_CART', { productId: bookDetails.id })
  }

  const seeOtherSellersClicked = async () => {
    const deliveryMethod = bookIsAvailable && hasShippingSeller ? 'shipping' : 'meetup'

    navigate.productSellersPage(bookDetails.slug, deliveryMethod)
  }

  const seeOtherVersionsClicked = () => {
    if (bookDetails?.versions?.length) {
      if (bookDetails?.versions.length > 1) {
        return setVersionsModalShow(true)
      }

      const version = bookDetails.versions[0]

      navigate.productPage(version.slug)
    }
  }

  const bookNotifyClicked = async () => {
    if (!token) {
      navigate.authPage()
      return
    }

    try {
      if (hasItem(bookDetails.id)) {
        setUnsubNotificationModalView(true)
      } else {
        await addItem(bookDetails.id)
        flashMessage.addFlashMessage(t({ id: 'waitingList.successMessage' }), 'success')
      }
    } catch (error) {
      flashMessage.addFlashMessage((error as Error).message, 'error')
    }
  }

  const getUnavailableText = () => {
    if (bookIsAvailable && !hasMeetupSeller && !bookHasOtherVersions) {
      return t({ id: 'bdp.versionsNotFound', defaultMessage: 'We could not find any other versions of this book' })
    }
    if (!bookIsAvailable && hasMeetupSeller) {
      return t({
        id: 'bdp.shippingSellersNotFound',
        defaultMessage: 'We could not find any sellers that offer shipping for this book'
      })
    }
    if (!bookIsAvailable) {
      return t({ id: 'bdp.sellersNotFound', defaultMessage: 'We could not find any sellers for this book' })
    }
  }

  useIntercom({ hideLauncher: false })

  return (
    <Box>
      {!isLoading && !bookIsAvailable && (
        <BuyButton onClick={seeOtherSellersClicked} isUnavailable={true} variant="outlined" disabled>
          {getUnavailableText()}
        </BuyButton>
      )}

      <Box marginBottom={hasRecommendedSeller || isLoading ? '24px' : ''}>
        <SellerListingCard
          data={recommendedSellerListing}
          isLoading={isLoading}
          viewOptions={{
            isRecommended: recommendedSellerListing?.badges?.recommended,
            isHighlighted: true,
            showDeliveryMethod: true,
            showPriceInCta: false,
            showInfoButton: true
          }}
          onClick={recommendedSellerClicked}
          loggedInUserIsSeller={user?.uuid === recommendedSellerListing?.seller.uuid}
        />
      </Box>
      {!isLoading && bookIsAvailable && (
        <BuyButton onClick={seeOtherSellersClicked} variant={hasShippingSeller ? 'outlined' : 'contained'}>
          {hasMeetupSeller && !hasShippingSeller
            ? t({ id: 'common.see_meetup_sellers', defaultMessage: 'See all meetup sellers' })
            : t({ id: 'common.see_sellers', defaultMessage: 'See all sellers' })}
        </BuyButton>
      )}
      {bookHasOtherVersions && (
        <BuyButton
          onClick={seeOtherVersionsClicked}
          variant={getOtherVersionsButtonVariant(
            isLoading,
            Boolean(bookIsAvailable || hasMeetupSeller),
            hasRecommendedSeller
          )}
        >
          {t(
            {
              id: 'productPage.button.seeOtherVersions',
              defaultMessage: '{count, plural, one {See # more version} other {See # more versions}}'
            },
            { count: bookDetails.versions?.length }
          )}
        </BuyButton>
      )}
      {!isLoading && (
        <>
          {(!bookIsAvailable && !hasMeetupSeller) || hasItem(bookDetails.id) ? (
            <BuyButton onClick={bookNotifyClicked} variant={bookHasOtherVersions ? 'outlined' : 'contained'}>
              {hasItem(bookDetails.id)
                ? t({ id: 'bdp.buyerActions.bookAvailableNotifyDisable' })
                : t({ id: 'bdp.buyerActions.bookAvailableNotify' })}
            </BuyButton>
          ) : null}
          <AlertsUnsubscribe
            action={async () => {
              await removeItem(bookDetails.id)
              setUnsubNotificationModalView(false)
              flashMessage.addFlashMessage(t({ id: 'waitingList.unsubscribeSuccess' }), 'success')
            }}
            isOpen={unsubNotificationModalView}
            onClose={() => setUnsubNotificationModalView(false)}
          />
        </>
      )}
      <ChooseDeliveryServiceModal />
    </Box>
  )
}

export default MainActions
